import { useTranslation } from 'react-i18next';
import InputShortText from '../InputShortText/InputShortText';
import InputNumber from '../InputNumber/InputNumber';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import CommissionTemplateFeeFields from '../CommissionContractFields/CommissionTemplateFeeFields';
import { CommissionContractOfferDto } from '../../api/CommissionContractOffersAPI';
import {
  DEFAULT_BRIDE_SPLIT_PERCENTAGE,
  DEFAULT_SALON_SPLIT_PERCENTAGE,
} from '../../hooks/commissionOfferEditor';

interface CommissionOfferFormProps {
  offer: CommissionContractOfferDto;
  onChange: (offer: CommissionContractOfferDto) => void;
  formKey?: string | number;
}

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

export default function CommissionOfferForm({
  offer,
  onChange,
  formKey = 'new',
}: CommissionOfferFormProps) {
  const { t } = useTranslation();
  const salonSplit = offer.salonSplitPercentage ?? DEFAULT_SALON_SPLIT_PERCENTAGE;
  const brideSplit = offer.brideSplitPercentage ?? DEFAULT_BRIDE_SPLIT_PERCENTAGE;

  return (
    <div className="space-y-6">
      <InputShortText
        key={`name-${formKey}`}
        label={t('commission_payment_offers.name') || 'Title'}
        defaultValue={offer.name}
        onChange={(e) => onChange({ ...offer, name: e.target.value })}
      />
      <RichTextEditor
        value={offer.customerDescription || ''}
        onChange={(customerDescription) => onChange({ ...offer, customerDescription })}
        label={t('commission_payment_offers.description') || 'Description (shown to brides)'}
        minHeight="160px"
        maxLength={2000}
      />
      <InputNumber
        label="commission_contract_template.sale_duration_label"
        helperText="commission_contract_template.sale_duration_hint"
        defaultValue={offer.saleDuration}
        onChange={(e) => onChange({ ...offer, saleDuration: Number(e.target.value) })}
        validationFn={(n) => Number.isFinite(n) && n >= 1 && Number.isInteger(n)}
        errorMessage="commission_payment_offers.sale_duration_invalid"
      />
      <div>
        <p className="text-sm font-extrabold">
          {t('commission_contract_template.commission_split_label')}:
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {t('commission_contract_template.commission_split_hint')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <InputNumber
            label="commission_contract_template.salon_percentage_label"
            defaultValue={salonSplit}
            onChange={(e) => {
              const value = clampPercent(Number(e.target.value));
              onChange({
                ...offer,
                salonSplitPercentage: value,
                brideSplitPercentage: 100 - value,
              });
            }}
            validationFn={(n) => n >= 0 && n <= 100}
            errorMessage="commission_payment_offers.split_invalid"
          />
          <InputNumber
            label="commission_contract_template.bride_percentage_label"
            defaultValue={brideSplit}
            onChange={(e) => {
              const value = clampPercent(Number(e.target.value));
              onChange({
                ...offer,
                brideSplitPercentage: value,
                salonSplitPercentage: 100 - value,
              });
            }}
            validationFn={(n) => n >= 0 && n <= 100}
            errorMessage="commission_payment_offers.split_invalid"
          />
        </div>
      </div>
      <CommissionTemplateFeeFields
        dropFee={{
          price: offer.dropFeePrice ?? 0,
          currency: offer.dropFeeCurrency ?? '',
        }}
        renewalFee={{
          price: offer.renewalFeePrice ?? 0,
          currency: offer.renewalFeeCurrency ?? '',
        }}
        onDropFeeChange={(fee) =>
          onChange({ ...offer, dropFeePrice: fee.price, dropFeeCurrency: fee.currency })
        }
        onRenewalFeeChange={(fee) =>
          onChange({ ...offer, renewalFeePrice: fee.price, renewalFeeCurrency: fee.currency })
        }
      />
    </div>
  );
}
